import { useState, useEffect, useCallback } from 'react';
import { coreApi } from '@shared/api/client';
import { CustomObjectDef, CreateCustomObjectInput } from './types';

const STORAGE_KEY = 'helios_custom_objects';

const INITIAL_FALLBACK_OBJECTS: CustomObjectDef[] = [
  {
    id: 'co-1',
    slug: 'partner_tier',
    label: 'Partner Tier',
    description: 'Channel partner levels, rebate agreements, and certification records.',
    icon: 'award',
    fields_count: 6,
    records_count: 3,
    created_at: new Date().toISOString(),
  },
  {
    id: 'co-2',
    slug: 'it_asset',
    label: 'Company Asset',
    description: 'Hardware, software licenses, and IT infrastructure assigned to employees.',
    icon: 'laptop',
    fields_count: 8,
    records_count: 4,
    created_at: new Date().toISOString(),
  },
];

function getStoredObjects(): CustomObjectDef[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {
    // ignore
  }
  return INITIAL_FALLBACK_OBJECTS;
}

export function useCustomObjects() {
  const [objects, setObjects] = useState<CustomObjectDef[]>(getStoredObjects);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchObjects = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await coreApi.get<CustomObjectDef[]>('/custom-objects');
      if (Array.isArray(data) && data.length > 0) {
        setObjects(data);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        window.dispatchEvent(new Event('helios-custom-objects-updated'));
      }
    } catch {
      // Keep local state
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchObjects();
  }, [fetchObjects]);

  const saveAndSync = (updated: CustomObjectDef[]) => {
    setObjects(updated);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      window.dispatchEvent(new Event('helios-custom-objects-updated'));
    } catch {
      // ignore
    }
  };

  const createObject = async (input: CreateCustomObjectInput) => {
    const newObj: CustomObjectDef = {
      ...input,
      id: `co-${Date.now()}`,
      fields_count: input.fields_count || 2,
      records_count: 0,
      created_at: new Date().toISOString(),
    };
    saveAndSync([newObj, ...objects]);
    try {
      await coreApi.post<CustomObjectDef>('/custom-objects', input);
    } catch {
      // local copy preserved
    }
  };

  const updateObject = async (id: string, input: Partial<CustomObjectDef>) => {
    const next = objects.map((o) =>
      o.id === id ? { ...o, ...input, updated_at: new Date().toISOString() } : o
    );
    saveAndSync(next);
    try {
      await coreApi.put<CustomObjectDef>(`/custom-objects/${id}`, input);
    } catch {
      // local copy preserved
    }
  };

  const deleteObject = async (id: string) => {
    const next = objects.filter((o) => o.id !== id);
    saveAndSync(next);
    try {
      await coreApi.delete(`/custom-objects/${id}`);
    } catch {
      // local copy preserved
    }
  };

  return {
    objects,
    isLoading,
    error,
    createObject,
    updateObject,
    deleteObject,
    reload: fetchObjects,
  };
}

export default useCustomObjects;
